import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {ISignboard, Signboard} from "/@lib@/commons/utils/signboard.js";
import {IActionable} from "/@back@/commons/actionables.js";

/**
 * Gestion des actions.
 *
 * Une action est un objet qui :
 * - décrit une action (label, icon, description, groupe d'appartenance...)
 * - pilote la visibilité et l'activation de l'action (selon des permissions, le contexte...)
 * - instancie et configure les widgets qui matérialisent l'action (ie factory des boutons HTML)
 * - exécute l'action.
 *
 * Une action a besoin d'un "actionContext" pour évaluer ses propriétés, construire un bouton ou être exécuté.
 *
 * Si l'actionContext possède une propriété "reg" (cf IRegPointer), c'est ce registre
 * qui est utilisé par l'action.
 *
 * Les racoucis-claviers (accelKey) sont gérés par un AccelKeyMgr qui gère l'association entre un racourci
 * clavier et une action. Plusieurs racourcis peuvent être associés à une même action. Pour permettre
 * l'affichage automatique d'un racourci clavier dans la construction des menus ou le tootip d'un bouton,
 * l'actionContext doit posséder une propriété supplémentaire "accelKeyMgr" (cf IAccelKeyMgrPointer).
 *
 */
export interface IAction<C> extends ISignboard<C> {

	/**
	 * Retourne true si cette action doit prendre la forme d'un menu.
	 * Les actions du menu seront obtenues via IActionMenu.getDatas();
	 * IMPORTANT : le type de l'action est invariant pour un couple action + actionContext.
	 * Autrement dit : les propiétés internes dynamiques du contexte ne peut faire évoluer le type de l'action.
	 * Note : une action de type menu n'est jamais exécutée: Action.execute() inutile.
	 */
	isMenu(ctx: C): this is IActionMenu<C>

	/**
	 * Retourne true si cette action est de type 'toggle': elle possède un état booléen on/off ou checkbox.
	 * L'état de l'action sera obtenu via IActionToggle.getDatas();
	 * IMPORTANT : le type de l'action est invariant pour un couple action + actionContext.
	 * Autrement dit : les propiétés internes dynamiques du contexte ne peut faire évoluer le type de l'action.
	 */
	isToggle(ctx: C): this is IActionToggle<C>

	/** Utilisé par les actions de type IActionMenu et IActionToggle. */
	getDatas(api: 'menu' | 'toggle' | string, ctx: C): any

	/**
	 * Factory d'un widget 'custom' de type button associé à cette action.
	 * Retourne :
	 * - undefined par défaut, l'implémentation standard sera utilisée.
	 * - null pour ne pas construire de widget.
	 */
	buildCustomButton(ctx: C, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined

	/**
	 * Permet à l'action d'enrichir le bouton (noeud DOM) avec des propriétés particulières non gérées en standard.
	 * Cette méthode est appelée par chaque widget une seule fois à l'initialisation.
	 *
	 * En fonction du contexte graphique, cette méthode pourrait être appelée avec un button
	 * qui n'a pas été instancié via Action.buildCustomButton().
	 */
	initButtonNode(buttonNode: Element, ctx: C): void

	/**
	 * Exécute l'action.
	 */
	execute(ctx: C, ev?: Event): any | 'noPreventDefault' | 'noStopPropag' | 'noStopPropag-noPreventDefault'

	/**
	 * Exécute l'action si elle est permise (visible et enabled).
	 */
	executeIfAvailable(ctx: C, ev?: Event): boolean

}

/**
 * Classe de base pour les objets Action.
 *
 * Chaque action possède :
 * - les propriétés pour spécifier un bouton ou un item de menu (label, icon, etc.),
 * - les propriétés pour afficher/masquer et activer/désactiver le bouton ou item de menu,
 * - la fonction pour executer l'action.
 */
export class Action<C = {}> extends Signboard<C> implements IAction<C> {

	isMenu(ctx: C): this is IActionMenu<C> {return false}

	isToggle(ctx: C): this is IActionToggle<C> {return false}

	getDatas(api: 'menu' | 'toggle' | string, ctx: C): any {return null}

	buildCustomButton(ctx: C, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {return undefined}

	initButtonNode(buttonNode: Element, ctx: C) {}

	/**
	 * A surcharger...
	 */
	execute(ctx: C, ev?: Event): any | 'noPreventDefault' | 'noStopPropag' | 'noStopPropag-noPreventDefault' {}

	executeIfAvailable(ctx: C, ev?: Event): boolean {
		if (this.isAvailable(ctx)) {
			this.execute(ctx, ev);
			return true;
		}
		return false;
	}

	setExecute(method: (this: Action<C>, ctx: C, ev?: Event) => any | 'noPreventDefault' | 'noStopPropag' | 'noStopPropag-noPreventDefault'): this {
		this.execute = method;
		return this;
	}

	/** Surcharge d'une propriété / méthode quelconque. */
	override(name: keyof Action<C>, val: any): this {
		this[name] = val;
		return this;
	}
}

type dynString<C> = (ctx: C, action: Action<C>) => string;

/** Contexte graphique pour l'instanciation d'un bouton. */
export type EButtonUiContext = 'bar' /* default */ | 'dialog' | 'menu' | 'custom' | string;

/** Typage pour une action de type Toggle. */
export interface IActionToggle<C> extends IAction<C> {
	/** null pour l'état 'mixed'. */
	getDatas(api: 'toggle', ctx: C): boolean | null
}

/** Typage pour une action de type Menu. */
export interface IActionMenu<C> extends IAction<C> {
	getDatas(api: 'menu', ctx: C): IAction<C>[]
}

/**
 * Représente une entrée dans un popup d'enumération avec marquage de l'entrée sélectionée
 * et désactivation de l'entrée si sélectionnée.
 */
export class EnumEntryAction<C> extends Action<C> implements IActionToggle<C> {

	/** Entrée sélectionnée (true) ou "mixte" (null). */
	selected: boolean | null;

	constructor(key: string, label: string, selected: boolean | null /*mixed*/) {
		super(key);
		this._label = label;
		this.selected = selected;
	}

	isToggle(ctx: C): this is IActionToggle<C> {return true}

	/** null pour l'état 'mixed'. */
	getDatas(api: 'toggle', ctx: C): boolean | null {return this.selected}

	isEnabled(ctx: C): boolean {
		if (this.selected) return false;
		return super.isEnabled(ctx);
	}
}

/** Action de type menu, listant des sous-actions. */
export class ActionMenu<C> extends Action<C> implements IActionMenu<C> {

	protected _actions: IAction<C>[] | ((ctx: C, action: IAction<C>) => IAction<C>[]);
	protected _actionLists: string | dynString<C>;
	protected _groupOrder: string | dynString<C>;

	isMenu(ctx: C): this is IActionMenu<C> {return true}

	/** Affectation directe du tableau d'actions ou d'une factory des actions. */
	setActions(actions: IAction<C>[] | ((ctx: C, action: IAction<C>) => IAction<C>[])): this {
		this._actions = actions;
		return this;
	}

	/** Affectation des actions par une liste d'extPoints. */
	setActionLists(actionLists: string | dynString<C>): this {
		this._actionLists = actionLists;
		return this;
	}

	/** Ordonancement des groupes d'actions. */
	setGroupOrder(groupOrder: string | dynString<C>): this {
		this._groupOrder = groupOrder;
		return this;
	}

	getGroupOrder(ctx: C): string {
		return (typeof this._groupOrder === 'function') ? this._groupOrder(ctx, this) : this._groupOrder;
	}

	getActions(ctx: C): IAction<C>[] {
		if (this._actions) return (typeof this._actions === 'function') ? this._actions(ctx, this) : this._actions;
		const actionLists = (typeof this._actionLists === 'function') ? this._actionLists(ctx, this) : this._actionLists;
		this._actions = actionLists ? ACTION.injectSepByGroup(REG.getReg(ctx).mergeLists<Action<C>>(...actionLists.split(' ')), this.getGroupOrder(ctx), ctx) : [];
		return this._actions;
	}

	getDatas(api: 'menu', ctx: C): IAction<C>[] {return this.getActions(ctx)}

}

/**
 * Action de type menu, listant des sous-actions, avec les propriétés "enabled" et "visible" dépendante
 * des actions filles (au moins une action fille active / visible pour être actif / visible).
 */
export class ActionMenuDep<C> extends ActionMenu<C> {
	isEnabled(ctx: C) {
		if (!super.isEnabled(ctx)) return false;
		for (const action of this.getActions(ctx)) {
			if (action.isEnabled(ctx) && !(action instanceof ActionSeparator)) return true;
		}
		return false;
	}

	isVisible(ctx: C) {
		if (!super.isVisible(ctx)) return false;
		for (const action of this.getActions(ctx)) {
			if (action.isVisible(ctx) && !(action instanceof ActionSeparator)) return true;
		}
		return false;
	}

	isInstantiable(ctx: C): boolean {
		for (const action of this.getActions(ctx)) {
			if (action.isInstantiable(ctx) && !(action instanceof ActionSeparator)) return true;
		}
		return false;
	}
}

/**
 * Action fondée sur un Signboard (généralement une IArea) pour constituer un bouton d'accès à
 * la View instanciée de cet area.
 */
export class ActionSignboardWrapper<C> extends Action<C> {

	protected _wrapped: ISignboard<C>;

	/**
	 * Doit renvoyer ACTION.NULL si pas de wrappedAction.
	 */
	getWrapped(ctx: C): ISignboard<C> {return this._wrapped || ACTION.NULL}

	getLabel(ctx: C): string {
		return this._label !== undefined ? super.getLabel(ctx) : this.getWrapped(ctx).getLabel(ctx);
	}

	getDescription(ctx: C): string {
		return this._description !== undefined ? super.getDescription(ctx) : this.getWrapped(ctx).getDescription(ctx);
	}

	getIcon(ctx: C): string {
		return this._icon !== undefined ? super.getIcon(ctx) : this.getWrapped(ctx).getIcon(ctx);
	}

	getGroup(ctx: C): string {
		return this._group !== undefined ? super.getGroup(ctx) : this.getWrapped(ctx).getGroup(ctx);
	}

	isVisible(ctx: C): boolean {
		if (this._visible !== undefined) return super.isVisible(ctx);
		if (!this.checkObjectRootVisiblePerm(ctx)) return false;
		return this.getWrapped(ctx).isVisible(ctx);
	}

	isEnabled(ctx: C): boolean {
		if (this._enabled !== undefined) return super.isEnabled(ctx);
		if (!this.checkObjectRootPerm(ctx)) return false;
		return this.getWrapped(ctx).isEnabled(ctx);
	}

	isInstantiable(ctx: C): boolean {
		if (this._instantiable !== undefined) return super.isInstantiable(ctx);
		return this.getWrapped(ctx).isInstantiable(ctx);
	}

}


/**
 * Wrapper d'une action.
 * L'action wrappée peut être initialisée par setOverridenSvc() via le mécanisme de surcharge
 * des svc des extPoints ou appelé explicitement.
 * La méthode getWrapped(context) peut aussi être surchargée.
 */
export class ActionWrapper<C> extends ActionSignboardWrapper<C> implements REG.IOverridingSvc {

	protected _wrapped: Action<C>;

	/**
	 * Doit renvoyer ActionWrapper.NULL si pas de wrappedAction.
	 */
	getWrapped(ctx: C): Action<C> {return this._wrapped || ACTION.NULL}

	/** Méthode utilisée par Reg.getSvc() pour affecter le svc surchargé avant de retourner le svc surchargeant. */
	setOverridenSvc(svc: any) {
		this._wrapped = svc;
		return this;
	}

	/** Rappel : cette propriété doit être stable pour le même objet ctx. */
	isMenu(ctx: C): this is IActionMenu<C> {
		return this.getWrapped(ctx).isMenu(ctx);
	}

	/** Rappel : cette propriété doit être stable pour le même objet ctx. */
	isToggle(ctx: C): this is IActionToggle<C> {
		return this.getWrapped(ctx).isToggle(ctx);
	}

	getDatas(api: string, ctx: C): any {
		return this.getWrapped(ctx).getDatas(api, ctx);
	}

	buildCustomButton(ctx: C, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		return this.getWrapped(ctx).buildCustomButton(ctx, uiContext, parent);
	}

	/**
	 * Attention: toutes les implémentations des actions wrappées doivent prendre en compte les
	 * précédentes initialisations qui auraient pu être réalisées par les autres actions wrappées
	 * (suite à un changement d'association de l'actionContext du widget).
	 */
	initButtonNode(buttonNode: Element, ctx: C) {
		this.getWrapped(ctx).initButtonNode(buttonNode, ctx);
	}

	execute(ctx: C, ev?: Event): any | 'noPreventDefault' | 'noStopPropag' | 'noStopPropag-noPreventDefault' {
		return this.getWrapped(ctx).execute(ctx, ev);
	}

}

/**
 * Wrapper d'une action obtenue dynamiquement via un svc d'un extPoints.
 * La méthode setWrappedSvc(svcCd) permet d'initialiser ce wrapper.
 */
export class ActionWrapperFromSvc<C> extends ActionWrapper<C> {
	protected _svcCd: string;

	getWrappedSvcCd() {return this._svcCd}

	setWrappedSvcCd(svcCd: string): this {
		this._svcCd = svcCd;
		return this;
	}

	getWrapped(ctx: C): Action<C> {
		return REG.getReg(ctx).getSvc<Action<C>>(this._svcCd) || ACTION.NULL;
	}
}


/**
 * Wrapper d'une action obtenue dynamiquement via une liste d'un reg.
 * Ce wrapper regroupe un ensemble d'actions iso-fonctionnelles correspondant à une seule action
 * vue d'un menu ou d'un racourci clavier mais impliquant diverses implémentations en fonction du contexte.
 * La liste des sous-actions est définie par une liste extPoints. L'ordre des actions dans la liste est importante
 * car la première action isAvailable() sera choisie (ou à défaut la 1ère isVisible()).
 */
export class ActionWrapperFromList<C> extends ActionWrapper<C> {
	protected _actionsList: string;

	getActionsList(ctx: C): string {
		return this._actionsList;
	}

	setActionsList(actionsList: string): this {
		this._actionsList = actionsList;
		return this;
	}

	getWrapped(ctx: C): Action<C> {
		const list = this.getList(ctx);
		let firstVisible = null;
		for (let i = 0; i < list.length; i++) {
			const subAction = list[i];
			if (subAction.isVisible(ctx)) {
				firstVisible = subAction;
				if (subAction.isEnabled(ctx)) return subAction;
			}
		}
		return firstVisible || ACTION.NULL;
	}

	getList(ctx: C): Action<C>[] {
		if (!this._actionsList) return [];
		return ((ctx as IRegPointer<any>).reg || REG.reg).getList<Action<C>>(this._actionsList) || [];
	}
}

/**
 * Action qui hack le contexte avant de le passer à la sous-action.
 * ATTENTION :
 * - la méthode à implémenter wrapCtx() est TRES souvent appelée, elle doit être optimisée.
 * - cette classe n'est pas un Wrapper qui permet de remplacer des proproétés de la sous-action via le méthodes setXxx().
 *   Solution : surcharger les méthodes getXxx(ctx) directement.
 *   Note : autre approche, envisager ActionWrapperHackCtx en déclinant toutes les méthodes de la forme :
 *   getLabel(ctx: C): string {return this._label !== undefined ? super.getLabel(ctx) : this.sub.getLabel(this.wrapCtx(ctx))}
 */
export abstract class ActionHackCtx<C, CSUB> extends Action<C> {

	constructor(public sub: IAction<CSUB>) {
		super(sub.getId());
	}

	abstract wrapCtx(ctx: C): CSUB;

	getLabel(ctx: C): string {return this.sub.getLabel(this.wrapCtx(ctx))}

	getIcon(ctx: C): string {return this.sub.getIcon(this.wrapCtx(ctx))}

	getDescription(ctx: C): string {return this.sub.getDescription(this.wrapCtx(ctx))}

	isVisible(ctx: C): boolean {return this.sub.isVisible(this.wrapCtx(ctx))}

	isEnabled(ctx: C): boolean {return this.sub.isEnabled(this.wrapCtx(ctx))}

	isInstantiable(ctx: C): boolean {return this.sub.isInstantiable(this.wrapCtx(ctx))}

	getGroup(ctx: C): string {return this.sub.getGroup(this.wrapCtx(ctx))}

	getSkin(ctx: C): string {return this.sub.getSkin(this.wrapCtx(ctx))}

	getSkinOver(ctx: C): string {return this.sub.getSkinOver(this.wrapCtx(ctx))}

	getDatas(api: "menu" | "toggle" | string, ctx: C): any {return this.sub.getDatas(api, this.wrapCtx(ctx))}

	getLastDatasKey(ctx: C): string {return this.sub.getLastDatasKey(this.wrapCtx(ctx))}

	isMenu(ctx: C): this is IActionMenu<C> {return this.sub.isMenu(this.wrapCtx(ctx))}

	isToggle(ctx: C): this is IActionToggle<C> {return this.sub.isToggle(this.wrapCtx(ctx))}

	initButtonNode(buttonNode: Element, ctx: C) {return this.sub.initButtonNode(buttonNode, this.wrapCtx(ctx))}

	buildCustomButton(ctx: C, uiContext: "bar" | "dialog" | "menu" | "custom" | string, parent?: Element): Element | null | undefined {
		return this.sub.buildCustomButton(this.wrapCtx(ctx), uiContext, parent);
	}

	execute(ctx: C, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		return this.sub.execute(this.wrapCtx(ctx), ev);
	}
}

/** Affectation d'un registre dans le contexte graphique de l'action */
export class ActionWrapEltReg<ELT extends Element & IRegPointer<any>, CSUB extends IRegPointer<any>, C extends IRegPointer<any> = {}> extends ActionHackCtx<C, CSUB> {
	constructor(sub: IAction<CSUB>, public reg: IReg<any>) {
		super(sub);
	}

	wrapCtx(ctx: C): CSUB {
		return Object.assign({} as CSUB, ctx, {reg: this.reg}) as CSUB
	}

	initButtonNode(buttonNode: ELT, ctx: C) {
		buttonNode.reg = this.reg;
		this.sub.initButtonNode(buttonNode, this.wrapCtx(ctx));
	}
}

export class ActionSeparator extends Action<any> {
	static ID = "#SEP";

	constructor() {
		super(ActionSeparator.ID);
		this._enabled = false;
		this._group = ActionSeparator.ID;
		this._label = "";
	}

	buildCustomButton(ctx: {}, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		const hr = document.createElement('hr');
		(hr as any as IActionable<any>).action = this;
		(hr as any as IActionable<any>).disabled = true;
		return hr;
	}
}

export namespace ACTION {

	export const NULL = Object.freeze(new Action('#NULL').setVisible(false).setEnabled(false)) as Action<any>;

	/**
	 * Insertion des séperators selon la propriété "group" des actions dans un nouveau tableau retourné.
	 * Si des séparator sont déjà inclus dans la liste d'actions passés en paramètre, ils sont nettoyés.
	 * L'ordre de tri des groupes eux-mêmes est spécifié dans groupsOrder.
	 *
	 * @param actions Tableau JS d'objets de type Action à trier.
	 * @param groupsOrder String représentant l'ordre des groupes séparés par des espaces,
	 *      "*" pour spécifier le point d'insertion des des groupes inconnus :
	 *      Exemple: "group1 group2 * groupEnd"
	 *      Note : les actions sans groupes peuvent être regroupées via "#none".
	 * @return tableau trié
	 */
	export function injectSepByGroup<C>(actions: IAction<C>[], groupsOrder: string, ctx: C): IAction<C>[] {
		if (!actions) return [];
		const grpByCd = {} as Dict<IAction<C>[]>;
		let grpStart: IAction<C>[][];
		const grpAny = [] as IAction<C>[][];
		let grpEnd: IAction<C>[][];
		const names = !groupsOrder || groupsOrder === '*' ? null : groupsOrder.split(' ');
		let offsetAny = names ? names.indexOf('*') : 0;
		if (offsetAny < 0) offsetAny = names.length;
		for (let i = 0; i < actions.length; i++) {
			const action = actions[i];
			const group = action.getGroup(ctx) || "";
			if (group === ActionSeparator.ID) continue; //on cleanup les separator actuels.
			const list = grpByCd[group];
			if (!list) {
				//nouveau groupe
				if (names) {
					const idx = names.indexOf(group);
					if (idx >= 0) {
						//groupe dans la liste ordonnée
						if (idx < offsetAny) {
							if (!grpStart) grpStart = [];
							grpStart[idx] = grpByCd[group] = [action];
						} else {
							if (!grpEnd) grpEnd = [];
							grpEnd[idx - offsetAny] = grpByCd[group] = [action]
						}
						continue;
					}
				}
				grpAny.push((grpByCd[group] = [action]));
			} else list.push(action);
		}
		const result = [] as IAction<C>[];
		if (grpStart) for (const g of grpStart) if (g) result.push(...g, new ActionSeparator());
		for (const g of grpAny) result.push(...g, new ActionSeparator());
		if (grpEnd) for (const g of grpEnd) if (g) result.push(...g, new ActionSeparator());
		if (result.length > 0) result.length = result.length - 1; //on suppr le dernier SEPARATOR.
		return result;
	}

	export function isAccelPressed(ev: KeyboardEvent | MouseEvent): boolean {return IS_MAC ? ev.metaKey : ev.ctrlKey}

	export function isAnyControlPressed(ev: KeyboardEvent | MouseEvent, exceptShift: boolean = false): boolean {return ev.ctrlKey || ev.metaKey || ev.altKey || (!exceptShift && ev.shiftKey)}

	/** drag&drop : dupliquer au lieu de déplacer ? */
	export function isCopyOverMove(ev: DragEvent): boolean {return IS_MAC ? ev.altKey : ev.ctrlKey}

	/** Retourne un accelKeyMgr du cache du reg ou l'instancie. */
	export function mergeAccelKeyMgr<C>(reg: IReg<any>, ...list: string[]): AccelKeyMgr<C> | null {
		const id = list.length === 1 ? list[0] : list.join();
		let ak = reg.cache.get(id) as AccelKeyMgr<C>;
		if (ak === undefined) {
			const actions = reg.mergeListsAsMap(...list) as Dict<Action<C>>;
			ak = actions && Object.keys(actions).length > 0 ? new AccelKeyMgr<C>().initFromMapActions(actions) : null;
			reg.cache.set(id, ak);
		}
		return ak;
	}

	export function getAccelText(info: { mainKey: string, accelKey?: boolean, ctrlKey?: boolean, shiftKey?: boolean, altKey?: boolean, metaKey?: boolean }) {
		let text = info.mainKey || "";
		if (text) {
			if (info.shiftKey) text = (IS_MAC ? "\u21E7 " : "Shift+") + text;
			if (info.altKey) text = (IS_MAC ? "\u2325 " : "Alt+") + text;
			if (info.ctrlKey || (info.accelKey && !IS_MAC)) text = (IS_MAC ? "\u005E " : "Ctrl+") + text;
			if (info.metaKey || (info.accelKey && IS_MAC)) text = (IS_MAC ? "\u2318 " : "Meta+") + text;
		}
		return text;
	}

	/**
	 * Interne. Retourne une chaine localisée d'une key.
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
	 * @see https://developer.mozilla.org/fr/docs/Web/API/KeyboardEvent/key/Key_Values
	 */
	export function getLocalizedKey(key: string): string {
		return LOCALIZED_KEYS[key] || key;
	}

	/** Traitements génériques du résultat de IAction.execute(). */
	export function doOnEvent(r: any | 'noPreventDefault' | 'noStopPropag' | 'noStopPropag-noPreventDefault', ev: Event | null): any {
		if (!ev) return r;
		if (!r) {
			// Cet accelKeyMgr a trouvé et executé une action, on stop la propagation de l'event pour empêcher qu'un autre
			// accelKeyMgr parent traite aussi cet event.
			// L'exécution doit retourner 'noStopPropag' pour empêcher cet appel à ev.stopImmediatePropagation()
			ev.stopImmediatePropagation();
			// preventDefault() est nécessaire dans la très grande majorité des cas (effets de bords sévère sinon avec ctrl+s, ctrl+z, etc.).
			// L'exécution doit retourner 'noPreventDefault' pour empêcher cet appel à ev.preventDefault()
			if (ev.cancelable) ev.preventDefault();
		} else {
			//Cas rares d'exclusion du stopImmediatePropagation() et/ou du preventDefault()
			if (r !== 'noStopPropag' && r !== 'noStopPropag-noPreventDefault') ev.stopImmediatePropagation();
			if (ev.cancelable && r !== 'noPreventDefault' && r !== 'noStopPropag-noPreventDefault') ev.preventDefault();
		}
		return r;
	}

	/** Symbole pour l'enrichissement d'un contexte d'actions par la correction ortho (Electron seulement) */
	export const spellcheckSymbol = Symbol("spellcheck");
}

export interface ISpellcheckContextPointer {
	[ACTION.spellcheckSymbol]?: ISpellcheckContext;
}

export interface ISpellcheckContext {
	misspelledWord: string,
	dictionarySuggestions: string[]
}

/**
 * Gestionnaire pour les racourcis claviers.
 */
export class AccelKeyMgr<C> {
	protected _accelKeys: Map<number, AccelKey> = new Map();
	protected _anyAccelKeys: Map<number, AccelKey> = new Map();
	protected _accelKeyParent: AccelKeyMgr<C> | AccelKeyMgr<C>[];

	setAccelKeyMgrParent(parent: AccelKeyMgr<C> | AccelKeyMgr<C>[]): this {
		this._accelKeyParent = parent;
		return this;
	}

	/**
	 * Ajoute un racourci clavier.
	 * @param key Caractère ou une constante définit par LOCALIZED_KEYS.
	 * @param modifiers "" (aucun modifiers) OU "any" (n'importquel modifier)
	 * 		OU une combinaison de "ctrl" "shift", "alt", "meta", "accel" séparés par un '+'.
	 *    "accel" permet d'abstraire l'OS : synonyme de "meta" sur MAC, "ctrl" sinon.
	 */
	addAccelKey(key: string, modifiers: string, action: IAction<C>): this {
		const accelKey = new AccelKey(key, modifiers, action);
		let idx = 0;
		if (accelKey.charCode != 0) {
			idx |= 0x10;
			idx |= accelKey.charCode << 5;
		} else {
			idx |= accelKey.keyIndex << 5;
		}
		if (accelKey.isAnyModifier()) {
			this._anyAccelKeys.set(idx, accelKey);
		} else {
			if (accelKey.ctrlKey) idx |= 0x1;
			if (accelKey.shiftKey) idx |= 0x2;
			if (accelKey.altKey) idx |= 0x4;
			if (accelKey.metaKey) idx |= 0x8;
			this._accelKeys.set(idx, accelKey);
		}
		return this;
	}

	/**
	 * Init par une Map issue d'une liste d'extPoints par exemple.
	 * Syntaxe de la fullKey à respecter pour créer des clés uniques surchargeables dans l'extPoints list:
	 * {key} ( '-' {modifiers} )?
	 * avec:
	 * - key = Caractère en minusucle ou une constante définit dans LOCALIZED_KEYS.
	 * - modifiers : "any" (n'importequel modifier)
	 *    OU une combinaison dans cet ordre de "alt", ("ctrl", "meta" | "accel"), "shift" séparés par un '+'.
	 *    "accel" permet d'abstraire l'OS : synonyme de "meta" sur MAC, "ctrl" sinon.
	 */
	initFromMapActions(mapActions: Dict<IAction<C>>): this {
		if (mapActions) for (const fullKey in mapActions) {
			const sep = fullKey.indexOf("-", 1);
			const key = sep >= 1 ? fullKey.substring(0, sep) : fullKey;
			const modifiers = sep >= 1 ? fullKey.substring(sep + 1) : "";
			this.addAccelKey(key, modifiers, mapActions[fullKey]);
		}
		return this;
	}

	/** Reset pour reinit. */
	reset() {
		this._accelKeys.clear();
		this._anyAccelKeys.clear();
	}


	// listenEventsOnWindow(): this {
	// 	window.document.addEventListener("keydown", (ev: KeyboardEvent) => {
	// 		this.handleKeyboardEvent(ev, window);
	// 	});
	// 	return this;
	// }

	/**
	 * Traite un event clavier ("keydown") pour rechercher un accelKey et l'exécuter l'action associée.
	 *
	 * @param ev Event clavier.
	 * @param ctx Contexte pour l'exécution des actions.
	 * @return  0 aucune accelKey trouvée
	 *      1 accelKey trouvé mais action inactive
	 *      2 accelKey trouvée et exécutée.
	 */
	handleKeyboardEvent(ev: KeyboardEvent, ctx: C): number {
		let idx = 0;
		if (ev.key.length === 1 && ev.key !== ' ') {
			idx |= 0x10;
			idx |= ev.key.toLowerCase().charCodeAt(0) << 5;
		} else {
			idx |= getIndexKey(ev.key) << 5;
		}
		let accelKey = this._anyAccelKeys.get(idx);
		if (accelKey) {
			if (accelKey.action.isAvailable(ctx)) {
				ev.stopImmediatePropagation();
				const r = accelKey.action.execute(ctx, ev);
				if (ev.cancelable && r !== 'noPreventDefault') ev.preventDefault();
				return 2;
			}
			return 1;
		}
		if (ev.ctrlKey) idx |= 0x1;
		if (ev.shiftKey) idx |= 0x2;
		if (ev.altKey) idx |= 0x4;
		if (ev.metaKey) idx |= 0x8;
		//console.log("handleKeyboardEvent:::", idx, ev);
		accelKey = this._accelKeys.get(idx);
		if (accelKey) {
			//console.log("handleKeyboardEvent::found:::", accelKey, accelKey.action.isAvailable(ctx));
			if (accelKey.action.isInstantiable(ctx) && accelKey.action.isAvailable(ctx)) {
				ACTION.doOnEvent(accelKey.action.execute(ctx, ev), ev);
				return 2;
			}
			return 1;
		}
		if (this._accelKeyParent) {
			if (Array.isArray(this._accelKeyParent)) {
				for (const a of this._accelKeyParent) {
					const r = a.handleKeyboardEvent(ev, ctx);
					if (r !== 0) return r;
				}
				return 0
			}
			return this._accelKeyParent.handleKeyboardEvent(ev, ctx);
		}
		return 0;
	}

	/**
	 * Permet de retrouver le libellé localisé d'un racourci clavier pour une action
	 * en vue d'être inséré dans le label d'un menu.
	 * @return "" si pas trouvé.
	 */
	searchAccelText(action: IAction<C>): string {
		function checkRegisterAccelKey(accelKey: AccelKey) {
			if (accelKey?.action.getId() === Signboard.ID_UNDEFINED) {
				console.warn(`AccelKey '${accelKey.getAccelText()}' register in action without ID : `, accelKey.action);
			}
		}

		const id = action.getId();
		if (id === Signboard.ID_UNDEFINED) return "";//Risque fort de retourner un résultat faux pour des actions sans id
		for (const accelKey of this._accelKeys.values()) {
			if (id === accelKey.action.getId()) return accelKey.getAccelText();
			checkRegisterAccelKey(accelKey);
		}
		for (const accelKey of this._anyAccelKeys.values()) {
			if (id === accelKey.action.getId()) return accelKey.getAccelText();
			checkRegisterAccelKey(accelKey);
		}
		if (this._accelKeyParent) {
			if (Array.isArray(this._accelKeyParent)) {
				for (const a of this._accelKeyParent) {
					const r = a.searchAccelText(action);
					if (r !== "") return r;
				}
				return ""
			}
			return this._accelKeyParent.searchAccelText(action);
		}
		return "";
	}

}

/**
 * Reférence à un AccelKeyMgr.
 * Permet à un objet Context de l'api action de fournir le AccelKeyMgr
 * à utiliser pour injecter les racourcis clavier dans les libellés.
 */
export interface IAccelKeyMgrPointer<C> {
	accelKeyMgr?: AccelKeyMgr<C>
}

export function isAccelKeyMgrPointer<C>(c: any): c is Required<IAccelKeyMgrPointer<C>> {return c && c.accelKeyMgr}

/**
 * Interne. Réprésente un raccourci clavier pour une action donnée.
 */
class AccelKey {

	readonly modifiers: string;
	readonly charCode: number;
	readonly keyIndex: number;

	protected _accelText: string;

	constructor(readonly key: string, modifiers: string, readonly action: IAction<any>) {
		this.modifiers = modifiers || "";
		if (key.length === 1 && key !== ' ') {
			this.charCode = key.toLowerCase().charCodeAt(0);
			this.keyIndex = 0;
		} else {
			this.charCode = 0;
			this.keyIndex = getIndexKey(key);
		}
	}

	isAnyModifier(): boolean {return this.modifiers === "any"}

	get mainKey(): string {return this.charCode > 0 ? this.key.toUpperCase() : ACTION.getLocalizedKey(this.key)}

	//	get ctrlKey(): boolean {return this.modifiers.indexOf("ctrl") >= 0 || (!IS_MAC && this.modifiers.indexOf("accel") >= 0)}
	get ctrlKey(): boolean {
		if (this.modifiers.indexOf("ctrl") >= 0) return true;
		if (IS_MAC) return this.modifiers.indexOf("alt") >= 0;
		return this.modifiers.indexOf("accel") >= 0;
	}

	get shiftKey(): boolean {return this.modifiers.indexOf("shift") >= 0}

	//get altKey(): boolean {return this.modifiers.indexOf("alt") >= 0}
	get altKey(): boolean {
		if (IS_MAC && this.modifiers.indexOf("ctrl") < 0) return false;
		return this.modifiers.indexOf("alt") >= 0;
	}

	get metaKey(): boolean {return this.modifiers.indexOf("meta") >= 0 || (IS_MAC && this.modifiers.indexOf("accel") >= 0)}

	/** Construit une chaine localisée pour insertion dans les menus */
	getAccelText() {
		if (this._accelText !== undefined) return this._accelText;
		return this._accelText = ACTION.getAccelText(this);
	}
}

function getIndexKey(key: string): number {
	return INDEX_KEYS[key] || 0;
}

const LOCALIZED_KEYS: Dict<string> = {
	"Help": "Aide",
	"Backspace": "Arr.",
	"Insert": "Ins.",
	"Delete": "Suppr.",
	"Enter": "Entrée",
	"Pause": "Pause",
	"Escape": "Echap",
	"PageUp": "Page haut",
	"PageDown": "Page bas",
	"End": "Fin",
	"Home": "Début",
	"ArrowLeft": "Flèche gauche",
	"ArrowUp": "Flèche haut",
	"ArrowRight": "Flèche droite",
	"ArrowDown": "Flèche bas",
	"PrintScreen": "Impr",
	"F1": "F1",
	"F2": "F2",
	"F3": "F3",
	"F4": "F4",
	"F5": "F5",
	"F6": "F6",
	"F7": "F7",
	"F8": "F8",
	"F9": "F9",
	"F10": "F10",
	"F11": "F11",
	"F12": "F12",
	"F13": "F13",
	"F14": "F14",
	"F15": "F15",
	"F16": "F16",
	" ": "Espace", //KeyboardEvent.key == ' '
};

const INDEX_KEYS: Dict<number> = {};
(function () {
	let i = 0;
	for (const k in LOCALIZED_KEYS) INDEX_KEYS[k] = ++i;
})();

const IS_MAC = navigator.platform.indexOf("Mac") >= 0;

